import { forwardRef, useId, useLayoutEffect, useState } from 'react'
import { ImageBlock, type CommonBlockProps } from '../../../types'
import { useMessageContext } from '../../../contexts'
import { getImageSize } from '../../../utils'
import { webchatClasses } from '../../../styles/classes'
import { ImageOff } from 'lucide-react'
import { Skeleton } from '../../Skeleton'
import { useAsync } from 'react-use'

type Props = ImageBlock & CommonBlockProps
export const ImageComponent = forwardRef<HTMLImageElement, Props>(
  ({ url, type, orientation = 'auto', ...props }, ref) => {
    const id = useId()
    const { setIsLoading } = useMessageContext()
    const [imageOrientation, setImageOrientation] = useState(orientation === 'auto' ? 'square' : orientation)
    const [imgState, setImageState] = useState<'initial' | 'loaded' | 'error'>('initial')

    const {
      message: {
        blocks: { image },
      },
    } = webchatClasses

    useLayoutEffect(() => {
      setIsLoading((prev) => [...prev, id])
      return () => {
        setIsLoading((prev) => prev.filter((currId) => currId !== id))
      }
    }, [id, setIsLoading])

    useAsync(async () => {
      if (!url) return

      const img = new Image()

      try {
        const { width, height } = await getImageSize(url)

        if (orientation === 'auto') {
          setImageOrientation(getClosestAspectRatio(width, height))
        }

        img.onload = () => {
          setImageState('loaded')
          setIsLoading((prev) => prev.filter((currId) => currId !== id))
        }

        img.onerror = () => {
          setImageState('error')
          setImageOrientation('square')
          setIsLoading((prev) => prev.filter((currId) => currId !== id))
        }

        img.src = url

        // Handle race condition: image may already be loaded from cache
        if (img.complete) {
          if (img.naturalWidth > 0) {
            setImageState('loaded')
            setIsLoading((prev) => prev.filter((currId) => currId !== id))
          } else {
            setImageState('error')
            setImageOrientation('square')
            setIsLoading((prev) => prev.filter((currId) => currId !== id))
          }
        }
      } catch (error) {
        console.error(error)
        setImageState('error')
        setIsLoading((prev) => prev.filter((currId) => currId !== id))
      }

      return () => {
        img.onload = null
        img.onerror = null
      }
    }, [url, id, setIsLoading, orientation])

    if (!url) return null

    if (imgState === 'error') {
      return (
        <div data-error="true" data-orientation={imageOrientation} {...props} {...image.image} ref={ref}>
          <ImageOff size="25%" strokeWidth="5%" />
        </div>
      )
    }

    if (imgState === 'initial') {
      return <Skeleton data-orientation={imageOrientation} {...image.placeholder} />
    }

    return (
      <img
        data-orientation={imageOrientation}
        data-loaded={imgState === 'loaded'}
        {...props}
        {...image.image}
        src={url}
        alt=""
        ref={ref}
      />
    )
  }
)

ImageComponent.displayName = 'Image'

function getClosestAspectRatio(width: number, height: number) {
  type aspects = keyof typeof aspects
  const aspects = {
    square: 1,
    portrait: 3 / 4,
    landscape: 4 / 3,
  } as const
  const aspect = width / height
  const closest = (Object.keys(aspects) as aspects[]).reduce((prev, curr) => {
    return Math.abs(aspects[curr] - aspect) < Math.abs(aspects[prev] - aspect) ? curr : prev
  })
  return closest
}
